import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useAuthStore } from './auth.js'
import { useLotteryStore } from './lottery.js'
import { useTicketOpsStore } from './ticketOpsStore.js'
import { buildTicketLifecycleRecords, parseTicketOperationError } from '../utils/ticketUi.js'

export const useSellerStore = defineStore('seller', () => {
  const authStore = useAuthStore()
  const lotteryStore = useLotteryStore()
  const ticketOpsStore = useTicketOpsStore()

  const loading = ref(false)
  const error = ref('')
  const message = ref('')
  const selectedLotteryId = ref('')
  const lastSuccessfulRefreshAt = ref('')
  const supportReference = ref('')
  const rowLocks = ref({})

  const sellerUserId = computed(() => Number(authStore.userId || authStore.user?.id || 0))

  const sellerAssignments = computed(() => {
    return (lotteryStore.assignments || []).filter((item) => Number(item?.user_id) === Number(sellerUserId.value))
  })

  const ownedRows = computed(() => {
    const rows = buildTicketLifecycleRecords({
      history: ticketOpsStore.ticketHistory,
      assignments: sellerAssignments.value,
      batches: lotteryStore.batches,
      drawHistory: lotteryStore.drawHistory,
    })
    return rows.filter((row) => Number(row?.ownerUserId) === Number(sellerUserId.value))
  })

  const counters = computed(() => {
    const rows = ownedRows.value
    return {
      assigned: rows.filter((row) => row.state === 'assigned').length,
      reserved: rows.filter((row) => row.state === 'reserved').length,
      sold: rows.filter((row) => row.state === 'sold' || row.state === 'winner').length,
      remaining: rows.filter((row) => row.state === 'available' || row.state === 'assigned').length,
    }
  })

  const setRowLocked = (ticketNumber, locked) => {
    rowLocks.value = {
      ...rowLocks.value,
      [String(ticketNumber)]: Boolean(locked),
    }
  }

  const isRowLocked = (ticketNumber) => Boolean(rowLocks.value[String(ticketNumber)])

  const loadSellerLottery = async (lotteryId) => {
    loading.value = true
    error.value = ''

    try {
      selectedLotteryId.value = String(lotteryId || '')
      await lotteryStore.fetchLotteryDetail(lotteryId)
      await Promise.all([
        lotteryStore.fetchAssignments(lotteryId),
        lotteryStore.fetchBatches(lotteryId),
        lotteryStore.fetchDrawHistory(lotteryId),
        ticketOpsStore.fetchTicketHistory(lotteryId),
      ])
      lastSuccessfulRefreshAt.value = new Date().toISOString()
      return { success: true }
    } catch (err) {
      error.value = parseTicketOperationError(err, 'Could not load seller ticket data.')
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const runSellerOperation = async ({ lotteryId, action, ticketNumbers }) => {
    if (ticketOpsStore.operationLoading) {
      return { success: false, code: 'OPERATION_IN_PROGRESS', error: 'Please wait until the current action completes.' }
    }

    message.value = ''
    error.value = ''
    supportReference.value = ''

    ;(Array.isArray(ticketNumbers) ? ticketNumbers : []).forEach((ticket) => setRowLocked(ticket, true))
    try {
      const result = await ticketOpsStore.performOperation(lotteryId, action, ticketNumbers)
      if (!result.success) {
        error.value = result.error
        if (result.summary?.failures?.[0]?.correlation_ref) {
          supportReference.value = String(result.summary.failures[0].correlation_ref)
        }
        return result
      }

      message.value = `${result.summary?.succeeded || 0} ticket(s) updated.`
      await Promise.all([
        lotteryStore.fetchBatches(lotteryId),
        ticketOpsStore.fetchTicketHistory(lotteryId),
      ])
      return result
    } catch (err) {
      error.value = parseTicketOperationError(err, 'Could not complete ticket action.')
      supportReference.value = String(err?.response?.data?.correlation_ref || err?.response?.headers?.['x-correlation-id'] || '')
      return { success: false, error: error.value }
    } finally {
      ;(Array.isArray(ticketNumbers) ? ticketNumbers : []).forEach((ticket) => setRowLocked(ticket, false))
    }
  }

  return {
    loading,
    error,
    message,
    selectedLotteryId,
    lastSuccessfulRefreshAt,
    supportReference,
    rowLocks,
    sellerAssignments,
    ownedRows,
    counters,
    loadSellerLottery,
    runSellerOperation,
    isRowLocked,
  }
})


